import { add, minus } from '@com/utils/math'
import type { AnimationAssetModel } from '@ve-pro/api/project/type'
import { AnimationType } from '@ve-pro/enums/animation'
import { SegmentType } from '@ve-pro/enums/segment'
import type { SegmentCollection } from '@ve-pro/types/track.type'

const supportAnimationSegment = [
  SegmentType.Sticker,
  SegmentType.Text,
  SegmentType.Video,
] as const
const supportAnimationInoutSegment = [
  SegmentType.Sticker,
  SegmentType.Text,
  SegmentType.Video,
] as const
const supportAnimationLoopSegment = [
  SegmentType.Sticker,
  SegmentType.Text,
  SegmentType.Video,
] as const

type PickSegment<
  Collection extends SegmentCollection,
  Types extends SegmentType,
> = Collection extends { type: Types } ? Collection : never

type SupportAnimationSegment = PickSegment<
  SegmentCollection,
  typeof supportAnimationSegment[number]
>
type SupportAnimationInoutSegment = PickSegment<
  SegmentCollection,
  typeof supportAnimationInoutSegment[number]
>
type SupportAnimationLoopSegment = PickSegment<
  SegmentCollection,
  typeof supportAnimationLoopSegment[number]
>

// 动画的默认时长
export const ANIMATION_DURATION = 500 // 0.5s
// 动画的最小时长
export const ANIMATION_DURATION_MIN = 100 // 0.1s
// 动画的最大时长
export const ANIMATION_DURATION_MAX = 60 * 1000 // 60s
// 单次动画/循环动画的最大时长
export const LOOP_ANIMATION_DURATION_MAX = 5 * 1000 // 5s

export function isSupportAnimation(
  segment: SegmentCollection,
): segment is SupportAnimationSegment {
  return supportAnimationSegment.includes(segment.type as any)
}

export function isSupportAnimationInout(
  segment: SegmentCollection,
): segment is SupportAnimationInoutSegment {
  return supportAnimationInoutSegment.includes(segment.type as any)
}

export function isSupportAnimationLoop(
  segment: SegmentCollection,
): segment is SupportAnimationLoopSegment {
  return supportAnimationLoopSegment.includes(segment.type as any)
}

function addSegmentAnimationInOrOut(
  segment: SupportAnimationSegment,
  type: AnimationType.In | AnimationType.Out,
  value: AnimationAssetModel,
): boolean {
  const inverseType = type === AnimationType.In ? 'anim_out' : 'anim_in'
  if (segment[inverseType] && segment.duration < ANIMATION_DURATION_MIN * 2) {
    return false
  }
  if (!segment[inverseType] && segment.duration < ANIMATION_DURATION_MIN) {
    return false
  }

  const setAnim = (duration: number) => {
    segment[type] = { ...value, duration }
  }

  const inverseAnim = segment[inverseType]
  if (inverseAnim) {
    const rest = minus(segment.duration, inverseAnim.duration)
    if (rest < ANIMATION_DURATION_MIN) {
      setAnim(ANIMATION_DURATION_MIN)
      inverseAnim.duration = minus(segment.duration, ANIMATION_DURATION_MIN)
    } else {
      setAnim(Math.min(rest, ANIMATION_DURATION))
    }
  } else {
    setAnim(Math.min(segment.duration, ANIMATION_DURATION))
  }

  // 入场出场动画和组合动画互斥
  if (isSupportAnimationInout(segment)) {
    segment.anim_inout = null
  }

  return true
}

function addSegmentAnimationInout(
  segment: SupportAnimationInoutSegment,
  value: AnimationAssetModel,
): boolean {
  if (segment.duration < ANIMATION_DURATION_MIN) {
    return false
  }

  // 组合动画：默认时长为当前视频的总时长
  const duration = Math.min(ANIMATION_DURATION_MAX, segment.duration)
  segment.anim_inout = { ...value, duration }

  // 组合动画和入场出场动画、循环动画互斥
  segment.anim_in = null
  segment.anim_out = null
  segment.anim_loop = null

  return true
}

// 循环动画
function addSegmentAnimationLoop(
  segment: SupportAnimationLoopSegment,
  value: AnimationAssetModel,
): boolean {
  if (segment.duration < ANIMATION_DURATION_MIN) {
    return false
  }

  // 组合动画：默认时长为当前视频的总时长
  const duration = Math.min(ANIMATION_DURATION, segment.duration)
  segment.anim_loop = { ...value, duration }

  // 循环动画和组合动画互斥
  if (isSupportAnimationInout(segment)) {
    segment.anim_inout = null
  }

  return true
}

export function addSegmentAnimation(
  segment: SupportAnimationSegment,
  type: AnimationType,
  value: AnimationAssetModel,
): boolean {
  if (type === AnimationType.Inout && isSupportAnimationInout(segment)) {
    return addSegmentAnimationInout(segment, value)
  } else if (type === AnimationType.In || type === AnimationType.Out) {
    return addSegmentAnimationInOrOut(segment, type, value)
  } else if (type === AnimationType.Loop && isSupportAnimationLoop(segment)) {
    return addSegmentAnimationLoop(segment, value)
  }

  return false
}

export interface AnimationDurationPercent {
  in: number
  out: number
}

/**
 * 获取动画进场出场时长之间的比例关系
 * @param segment
 * @returns
 */
export function getAnimationDurationPercent(
  segment: SegmentCollection,
): AnimationDurationPercent {
  let _in = 0
  let _out = 0
  if (isSupportAnimation(segment)) {
    const duration = add(
      segment.anim_in?.duration ?? 0,
      segment.anim_out?.duration ?? 0,
    )
    _in = segment.anim_in ? segment.anim_in.duration / duration : 0
    _out = segment.anim_out ? segment.anim_out.duration / duration : 0
  }
  return {
    in: _in,
    out: _out,
  }
}

/**
 * 根据时长的变化，更新Segment的动画时长
 * 出入场动画、组合动画，如果因为裁剪、分割、变速导致时长小于0.1s是不是就要删掉这个动画了
 * @param segment
 * @param percent
 */
export function updateSegmentAnimationDuration(
  segment: SegmentCollection,
  percent?: AnimationDurationPercent, // 进场出场占比
): void {
  if (isSupportAnimation(segment)) {
    percent = percent ?? getAnimationDurationPercent(segment)
    // 进出场动画总时长
    const duration = add(
      segment.anim_in?.duration ?? 0,
      segment.anim_out?.duration ?? 0,
    )

    // 进场动画和出场动画超出了元件的总时长(假设只要超出总时长，就意味着不可能包含组合动画)
    if (duration > segment.duration) {
      if (segment.anim_in) {
        const value = Math.floor(segment.duration * percent.in)
        if (value < ANIMATION_DURATION_MIN) {
          segment.anim_in = null
        } else {
          segment.anim_in.duration = value
        }
      }

      // 剩余的
      if (segment.anim_out) {
        const value = minus(segment.duration, segment.anim_in?.duration ?? 0)
        if (value < ANIMATION_DURATION_MIN) {
          segment.anim_out = null
        } else {
          segment.anim_out.duration = value
        }
      }
    }

    // 循环动画(不需要处理它的时长)

    // 组合动画
    if (isSupportAnimationInout(segment) && segment.anim_inout) {
      if (segment.duration < ANIMATION_DURATION_MIN) {
        segment.anim_inout = null
      } else {
        segment.anim_inout.duration = Math.min(
          segment.anim_inout.duration,
          segment.duration,
        )
      }
    }
  }
}

// 动画分割
export function splitSegmentAnimation(
  segment: SegmentCollection,
  side: 'left' | 'right',
): void {
  // left -> in
  // right -> out
  const key = side === 'left' ? 'anim_in' : 'anim_out'
  const inverseKey = key === 'anim_in' ? 'anim_out' : 'anim_in'
  if (isSupportAnimation(segment)) {
    // 出入场动画
    const anim = segment[key]
    if (anim && anim.duration > segment.duration) {
      anim.duration = segment.duration
      // 在范围内裁剪，循环动画则被删除
      if (isSupportAnimationLoop(segment)) {
        segment.anim_loop = null
      }
    }

    segment[inverseKey] = null

    // 组合动画
    if (isSupportAnimationInout(segment) && segment.anim_inout) {
      if (side === 'left') {
        segment.anim_inout.duration = Math.min(
          segment.anim_inout.duration,
          segment.duration,
        )
      } else {
        segment.anim_inout = null
      }
    }
    updateSegmentAnimationDuration(segment)
  }
}
